import React, { useEffect } from 'react';
import styled, { keyframes } from 'styled-components';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowRight } from '@fortawesome/free-solid-svg-icons';
import { Helmet } from 'react-helmet-async';

// Animasyonlar
const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const slideInUp = keyframes`
  from {
    transform: translateY(50px);
    opacity: 0;
  }
  to {
    transform: translateY(0);
    opacity: 1;
  }
`;

const slideInLeft = keyframes`
  from {
    transform: translateX(-50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const slideInRight = keyframes`
  from {
    transform: translateX(50px);
    opacity: 0;
  }
  to {
    transform: translateX(0);
    opacity: 1;
  }
`;

const floatAnimation = keyframes`
  0% {
    transform: translateY(0px);
  }
  50% {
    transform: translateY(-10px);
  }
  100% {
    transform: translateY(0px);
  }
`;

const HomeContainer = styled.div`
  padding-top: 0;
`;

const HeroSection = styled.section`
  height: 100vh;
  display: flex;
  flex-direction: column;
  justify-content: center;
  background-color: #f5f5f5;
  padding: 0 50px;
  position: relative;
  overflow: hidden;
`;

const HeroBackground = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  z-index: 1;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: linear-gradient(135deg, rgba(245,245,245,0.8) 0%, rgba(245,245,245,0.9) 100%);
    z-index: 2;
  }
`;

const HeroContent = styled.div`
  position: relative;
  z-index: 3;
  animation: ${fadeIn} 1s ease-in-out;
  max-width: 1200px;
  margin: 0 auto;
`;

const MainTitle = styled(motion.h1)`
  font-size: 5rem;
  margin: 0;
  line-height: 1;
  background: linear-gradient(120deg, #2c3e50, #4a5568, #2d3748);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  font-weight: 800;
  letter-spacing: -0.03em;
  
  @media (max-width: 768px) {
    font-size: 3rem;
  }
`;

const ModernTitleContainer = styled(motion.div)`
  display: flex;
  flex-direction: column;
  margin-bottom: 2rem;
`;

const RoleContainer = styled(motion.div)`
  display: flex;
  flex-wrap: wrap;
  gap: 1rem;
  margin-top: 2rem;
  position: relative;
`;

const Role = styled(motion.div)`
  background-color: #ffffff;
  padding: 0.5rem 1.2rem;
  border-radius: 100px;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.05);
  font-weight: 600;
  font-size: 1rem;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-right: 10px;
  margin-bottom: 10px;
  color: #2d3748;
  border: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.3s ease;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 10px 15px rgba(0, 0, 0, 0.1);
  }
  
  &:nth-child(1) {
    background: linear-gradient(90deg, #e3f2fd, #bbdefb);
  }
  
  &:nth-child(2) {
    background: linear-gradient(90deg, #e8f5e9, #c8e6c9);
  }
  
  &:nth-child(3) {
    background: #2d3748;
    color: white;
  }
  
  &:nth-child(4) {
    background: linear-gradient(90deg, #f3e5f5, #e1bee7);
  }
  
  &:nth-child(5) {
    background: linear-gradient(90deg, #fffde7, #fff9c4);
  }
  
  &:nth-child(6) {
    background: linear-gradient(90deg, #fbe9e7, #ffccbc);
  }
  
  &:nth-child(7) {
    background: linear-gradient(90deg, #e0f7fa, #b2ebf2);
  }
`;

const SubtitleAccent = styled.span`
  font-weight: 400;
  opacity: 0.7;
  font-size: 1.2rem;
  margin-top: 1rem;
  display: block;
  color: #4a5568;
`;

const AboutSection = styled.section`
  padding: 100px 50px;
  background-color: #fff;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: #fff;
    clip-path: polygon(0 100%, 100% 100%, 100% 0);
  }
`;

const AboutTitle = styled(motion.h3)`
  font-size: 2rem;
  margin-bottom: 30px;
  position: relative;
  display: inline-block;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 50px;
    height: 3px;
    background-color: #000;
  }
`;

const AboutContent = styled(motion.div)`
  max-width: 800px;
  margin: 0 auto;
  line-height: 1.6;
  
  p {
    margin-bottom: 20px;
  }
`;

const Button = styled(Link)`
  display: inline-block;
  padding: 12px 24px;
  margin-top: 20px;
  background-color: #000;
  color: #fff;
  text-decoration: none;
  border-radius: 4px;
  transition: all 0.3s;
  position: relative;
  overflow: hidden;
  
  &:hover {
    background-color: #333;
    transform: translateY(-3px);
    box-shadow: 0 10px 20px rgba(0,0,0,0.1);
    
    svg {
      transform: translateX(3px);
    }
  }
  
  svg {
    margin-left: 8px;
    transition: transform 0.3s;
  }
  
  &:after {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(255,255,255,0.2);
    transform: scaleX(0);
    transform-origin: right;
    transition: transform 0.5s;
  }
  
  &:hover:after {
    transform: scaleX(1);
    transform-origin: left;
  }
`;

const ServicesSection = styled.section`
  padding: 100px 50px;
  background-color: #f9f9f9;
  position: relative;
`;

const ServicesTitle = styled(motion.h6)`
  font-size: 1rem;
  margin-bottom: 50px;
  text-transform: uppercase;
  font-weight: 600;
  letter-spacing: 2px;
  display: inline-block;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -10px;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #000;
  }
`;

const ServicesGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
  margin-top: 30px;
`;

const ServiceCard = styled(motion.div)`
  padding: 30px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  position: relative;
  z-index: 1;
  overflow: hidden;
  
  &:before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    width: 100%;
    height: 0;
    background: linear-gradient(to bottom, rgba(0,0,0,0.03) 0%, rgba(0,0,0,0) 100%);
    z-index: -1;
    transition: height 0.5s;
  }
  
  &:hover {
    transform: translateY(-10px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
    
    &:before {
      height: 100%;
    }
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  margin-bottom: 20px;
  
  img {
    width: 100%;
    height: auto;
    transition: transform 0.3s;
  }
  
  ${ServiceCard}:hover & img {
    transform: scale(1.1);
  }
`;

const ServiceTitle = styled.h3`
  font-size: 1.5rem;
  margin-bottom: 15px;
  position: relative;
  padding-bottom: 10px;
  
  &:after {
    content: '';
    position: absolute;
    bottom: 0;
    left: 0;
    width: 30px;
    height: 2px;
    background-color: #000;
    transition: width 0.3s;
  }
  
  ${ServiceCard}:hover &:after {
    width: 50px;
  }
`;

const ServiceDescription = styled.p`
  color: #666;
  line-height: 1.6;
  
  strong {
    color: #000;
  }
`;

const BlogSection = styled.section`
  padding: 100px 50px;
  background-color: #fff;
`;

const BlogTitle = styled(motion.h2)`
  font-size: 2rem;
  margin-bottom: 50px;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: #000;
  }
`;

const BlogGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr 1fr;
  gap: 30px;
  
  @media (max-width: 992px) {
    grid-template-columns: 1fr 1fr;
  }
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const BlogCard = styled(motion.div)`
  border-radius: 8px;
  overflow: hidden;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  background-color: #fff;
  position: relative;
  top: 0;
  
  &:hover {
    top: -10px;
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  }
`;

const BlogContent = styled.div`
  padding: 20px;
`;

const BlogPostTitle = styled.h4`
  font-size: 1.2rem;
  margin-bottom: 10px;
  transition: color 0.3s;
  
  ${BlogCard}:hover & {
    color: #555;
  }
`;

const BlogPostMeta = styled.div`
  font-size: 0.8rem;
  color: #666;
  margin-bottom: 10px;
  display: flex;
  align-items: center;
  
  span {
    margin-right: 10px;
  }
`;

const ReviewsSection = styled.section`
  padding: 100px 50px;
  background-color: #f5f5f5;
  position: relative;
  
  &:before {
    content: '';
    position: absolute;
    top: -50px;
    left: 0;
    width: 100%;
    height: 100px;
    background-color: #f5f5f5;
    clip-path: polygon(0 100%, 100% 100%, 100% 0);
  }
`;

const ReviewsTitle = styled(motion.h2)`
  font-size: 2rem;
  margin-bottom: 50px;
  text-align: center;
  position: relative;
  
  &:after {
    content: '';
    position: absolute;
    bottom: -15px;
    left: 50%;
    transform: translateX(-50%);
    width: 50px;
    height: 3px;
    background-color: #000;
  }
`;

const ReviewsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 30px;
`;

const ReviewCard = styled(motion.div)`
  padding: 30px;
  background-color: #fff;
  border-radius: 8px;
  box-shadow: 0 5px 15px rgba(0,0,0,0.05);
  transition: all 0.3s;
  position: relative;
  
  &:hover {
    transform: translateY(-5px);
    box-shadow: 0 15px 30px rgba(0,0,0,0.1);
  }
  
  &:before {
    content: '"';
    position: absolute;
    top: 20px;
    left: 20px;
    font-size: 60px;
    color: rgba(0,0,0,0.1);
    font-family: Georgia, serif;
  }
`;

const ReviewText = styled.p`
  font-style: italic;
  margin-bottom: 20px;
  line-height: 1.6;
  position: relative;
  z-index: 1;
`;

const ReviewAuthor = styled.p`
  font-weight: 600;
  display: flex;
  align-items: center;
  
  &:before {
    content: '';
    width: 20px;
    height: 1px;
    background-color: #000;
    margin-right: 10px;
    display: inline-block;
  }
`;

// Animasyon varyantları
const fadeInUp = {
  hidden: { opacity: 0, y: 60 },
  visible: { 
    opacity: 1, 
    y: 0,
    transition: { 
      duration: 0.6,
      ease: [0.6, -0.05, 0.01, 0.99]
    }
  }
};

const stagger = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      staggerChildren: 0.1
    }
  }
};

const HomePage = () => {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  
  return (
    <>
      <Helmet>
        <title>Dogukan Yaylaci - IT & Web Development Specialist</title>
        <meta name="description" content="Dogukan Yaylaci is an IT professional with over 5 years of experience as a Web Master and Creative Frontend Developer specializing in web design, backend development, and digital marketing." />
        <meta name="keywords" content="Dogukan Yaylaci, IT professional, web development, frontend development, cybersecurity, digital marketing" />
        <link rel="canonical" href="https://dogukanyaylaci.dev/" />
      </Helmet>
      
      <HomeContainer>
        <HeroSection>
          <HeroBackground />
          <HeroContent>
            <ModernTitleContainer
              initial="hidden"
              animate="visible"
              variants={stagger}
            >
              <MainTitle variants={fadeInUp}>Dogukan Yaylacı</MainTitle>
              <SubtitleAccent variants={fadeInUp}>Bringing Digital Ideas to Life</SubtitleAccent>
              
              <RoleContainer
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                transition={{ delay: 0.5, duration: 0.5 }}
              >
                <Role 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Web Master
                </Role>
                <Role 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  Creative Developer
                </Role>
                <Role 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  IT Specialist
                </Role>
                <Role 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  UI/UX Design
                </Role>
                <Role 
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                >
                  SEO Expert
                </Role>
              </RoleContainer>
            </ModernTitleContainer>
          </HeroContent>
        </HeroSection>
        
        <AboutSection>
          <AboutContent
            initial={{ opacity: 0, y: 50 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
            viewport={{ once: true, amount: 0.3 }}
          >
            <AboutTitle
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              I'm Dogukan
            </AboutTitle>
            <p>Welcome to my personal website! I'm an IT professional with over five years of experience as a Web Master and Creative Frontend Developer. My passion for digital literacy drives me to stay updated in web design, backend development, and SEO.</p>
            <p>As the owner of a technology magazine, I keep up with industry trends and share insights, enhancing my content creation skills. I've gained valuable experience in both office and remote settings, focusing on collaboration and project management.</p>
            <p>I'm particularly interested in artificial intelligence and its impact on development, as well as information and data security, programming, network and server security, and digital marketing.</p>
            <p>If you're looking for a developer or IT expert, or if you'd like to collaborate on web development, advertising, marketing, or SEO strategies, let's connect! I'm open to offers and work opportunities. You can click the link below for more information about me and to get in touch.</p>
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
            >
              <Button to="/contact">
                Get in touch <FontAwesomeIcon icon={faArrowRight} />
              </Button>
            </motion.div>
          </AboutContent>
        </AboutSection>
        
        <ServicesSection>
          <ServicesTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            My Abilities
          </ServicesTitle>
          <ServicesGrid>
            <ServiceCard
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <ServiceIcon>
                <img src="https://dogukanyaylaci.dev/wp-content/uploads/2023/10/GettyImages-1456747446-1.svg" alt="AI Content Writing" />
              </ServiceIcon>
              <ServiceTitle>AI Content Writing</ServiceTitle>
              <ServiceDescription>
                From transforming your content strategy to delivering engaging narratives and optimized text, our expert services include ➤ <strong>AI-Driven Content Creation</strong>, ➤ <strong>SEO Optimization</strong>, ➤ <strong>Personalized Content Solutions</strong>, and ➤ <strong>Data-Driven Insights</strong>. Let us help you captivate your audience with intelligent and compelling writing.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <ServiceIcon>
                <img src="https://dogukanyaylaci.dev/wp-content/uploads/2023/10/GettyImages-1456747446-2.svg" alt="Brand Strategy" />
              </ServiceIcon>
              <ServiceTitle>Brand Strategy</ServiceTitle>
              <ServiceDescription>
                Brand strategy is the blueprint for how a company presents itself to the world. A well-crafted strategy lays the ➤ <strong>Foundation</strong> for consistent brand experiences that resonate with target audiences.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <ServiceIcon>
                <img src="https://dogukanyaylaci.dev/wp-content/uploads/2023/10/GettyImages-1444653136.svg" alt="Proven Marketing" />
              </ServiceIcon>
              <ServiceTitle>Proven Marketing</ServiceTitle>
              <ServiceDescription>
                From cultivating engaging ➤ Social Media presence to precise direct and ➤ <strong>Product Marketing</strong>, we reach your ➤ <strong>Target Audience</strong> with compelling ➤ <strong>Content Creation</strong> strategies.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.4 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <ServiceIcon>
                <img src="https://dogukanyaylaci.dev/wp-content/uploads/2023/10/GettyImages-1207574548.svg" alt="Future-Proof Development" />
              </ServiceIcon>
              <ServiceTitle>Future-Proof Development</ServiceTitle>
              <ServiceDescription>
                Development is the art of crafting dynamic, interactive, and user-friendly ➤ <strong>Websites</strong> and ➤ <strong>Apps</strong>. It's about crafting seamless digital experiences that captivate audiences across various platforms.
              </ServiceDescription>
            </ServiceCard>
            
            <ServiceCard
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.5 }}
              viewport={{ once: true, amount: 0.3 }}
              whileHover={{ y: -10 }}
            >
              <ServiceIcon>
                <img src="https://dogukanyaylaci.dev/wp-content/uploads/2023/10/GettyImages-1207574548.svg" alt="Mastering IT Solutions" />
              </ServiceIcon>
              <ServiceTitle>Mastering IT Solutions</ServiceTitle>
              <ServiceDescription>
                IT Professionals are the backbone of modern technology, specializing in ➤ <strong>Systems Integration</strong> and ➤ <strong>Network Security</strong>. They create robust infrastructures and implement innovative solutions that drive efficiency and safeguard data, ensuring seamless operations across all business functions.
              </ServiceDescription>
            </ServiceCard>
          </ServicesGrid>
        </ServicesSection>
        
        <BlogSection>
          <BlogTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Blog
          </BlogTitle>
          <BlogGrid>
            <BlogCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
              as={Link}
              to="/blog/secure-access-service-edge"
            >
              <BlogContent>
                <BlogPostTitle>What is Secure Access Service Edge (SASE)?</BlogPostTitle>
                <BlogPostMeta>
                  <span>Doğukan Yaylacı</span> • <span>September 24, 2024</span>
                </BlogPostMeta>
                <p>In today's rapidly evolving digital landscape, businesses are more connected than ever, but with this connectivity comes increased security risks...</p>
              </BlogContent>
            </BlogCard>
            
            <BlogCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
              as={Link}
              to="/blog/ai-in-content-creation"
            >
              <BlogContent>
                <BlogPostTitle>The Role of AI in Modern Content Creation</BlogPostTitle>
                <BlogPostMeta>
                  <span>Doğukan Yaylacı</span> • <span>November 10, 2024</span>
                </BlogPostMeta>
                <p>Artificial Intelligence is revolutionizing how we create and consume content. This article explores the latest AI tools for content creators...</p>
              </BlogContent>
            </BlogCard>
            
            <BlogCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
              as={Link}
              to="/blog/web-security-best-practices"
            >
              <BlogContent>
                <BlogPostTitle>Web Security Best Practices for 2025</BlogPostTitle>
                <BlogPostMeta>
                  <span>Doğukan Yaylacı</span> • <span>October 5, 2024</span>
                </BlogPostMeta>
                <p>Cybersecurity threats continue to evolve. Learn the essential web security practices that every developer and IT professional should implement...</p>
              </BlogContent>
            </BlogCard>
          </BlogGrid>
          <motion.div
            style={{ textAlign: 'center', marginTop: '30px' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <Button to="/blog">
              MORE <FontAwesomeIcon icon={faArrowRight} />
            </Button>
          </motion.div>
        </BlogSection>
        
        <ReviewsSection>
          <ReviewsTitle
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            Reviews
          </ReviewsTitle>
          <ReviewsGrid>
            <ReviewCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.1 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <ReviewText>Working with Dogukan has been a game-changer for our online strategy. His expertise in SEO and web design has significantly improved our search engine rankings and user engagement. His innovative approach and dedication to staying ahead of digital trends make him an invaluable partner</ReviewText>
              <ReviewAuthor>Emily Johnson, Marketing Director at Bright Solutions</ReviewAuthor>
            </ReviewCard>
            
            <ReviewCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.2 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <ReviewText>Dogukan's technical skills and creative vision are unmatched. He transformed our website into a user-friendly, visually appealing platform that perfectly represents our brand. His proactive approach to digital marketing has driven impressive results. Highly recommended!</ReviewText>
              <ReviewAuthor>Michael Smith, Founder of Tech Innovators</ReviewAuthor>
            </ReviewCard>
            
            <ReviewCard
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.3 }}
              viewport={{ once: true, amount: 0.3 }}
            >
              <ReviewText>Our experience with Dogukan has been exceptional. His knowledge of SEO and digital marketing has helped us attract more clients and enhance our online presence. His professionalism and attention to detail make him a standout in the industry.</ReviewText>
              <ReviewAuthor>Sara Thompson, Owner of Thompson Law Group</ReviewAuthor>
            </ReviewCard>
          </ReviewsGrid>
        </ReviewsSection>
      </HomeContainer>
    </>
  );
};

export default HomePage; 